import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, DoorOpen, Wrench, CheckCircle, Clock, FileCheck, Users } from 'lucide-react';
import { GoogleReviewBanner } from '../components/GoogleReviewBanner';

export const Home = () => {
  useEffect(() => {
    // Initialize scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate class
    const animatedElements = document.querySelectorAll('.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: <Lock size={32} />,
      title: 'Locks & Security',
      description: 'Locksmith services, multipoint locking systems, repairs, replacements, and security upgrades.'
    },
    {
      icon: <DoorOpen size={32} />,
      title: 'Windows & Doors',
      description: 'Tilt and turn windows, tilt and slide doors, uPVC and aluminium repairs, hinges, handles, and alignment.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Glazing Services',
      description: 'Glass replacement, failed units, thermal upgrades, and acoustic glass solutions.'
    }
  ];

  const whyChoose = [
    {
      icon: <Wrench size={28} />,
      title: 'Repair-First Mindset',
      description: 'We fix the cause, not just the symptom. Expert diagnosis before any work begins.'
    },
    {
      icon: <CheckCircle size={28} />,
      title: 'Quality Guaranteed',
      description: 'Fully insured with guaranteed workmanship. Professional reporting and documentation.'
    },
    {
      icon: <Clock size={28} />,
      title: 'Minimal Hassle',
      description: 'Clear communication, reliable follow-through, and emergency call-out available.'
    },
    {
      icon: <Users size={28} />,
      title: 'Long-Term Partnerships',
      description: 'Trusted by letting agents, property managers, landlords, and homeowners across the UK.'
    }
  ];

  return (
    <div>
      {/* Google Review Banner */}
      <GoogleReviewBanner />
      
      {/* Hero Section — Dark Royal */}
      <section style={{
        position: 'relative',
        minHeight: '640px',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'var(--royal-dark-charcoal)',
        color: 'var(--text-on-dark-primary)'
      }} data-testid="hero-section">
        {/* Background image at low opacity */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <img 
            src="https://images.unsplash.com/photo-1767236798800-9e3675c824f4"
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.22, filter: 'grayscale(50%) contrast(1.1)' }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(26,26,26,0.75) 0%, rgba(26,26,26,0.92) 70%, var(--royal-dark-charcoal) 100%)'
          }} />
        </div>

        {/* Crown + Lock pattern texture */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0.07,
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'><g fill='none' stroke='%23D4AF37' stroke-width='1.2'><path d='M60 20l7 13 13-5-5 15H45l-5-15 13 5z'/><circle cx='60' cy='78' r='8'/><path d='M53 74v-5a7 7 0 0 1 14 0v5'/><rect x='50' y='78' width='20' height='17' rx='2'/></g></svg>\")",
          backgroundSize: '120px 120px'
        }} />

        {/* Right-side crown emblem watermark */}
        <div style={{
          position: 'absolute', right: '-80px', top: '50%', transform: 'translateY(-50%)',
          zIndex: 1, pointerEvents: 'none', opacity: 0.08
        }} aria-hidden="true">
          <img src="/brand/royal-logo-crown.png" alt="" style={{ width: '420px', height: 'auto' }} />
        </div>

        {/* Hero Content */}
        <div className="royal-container" style={{ position: 'relative', zIndex: 2, padding: '96px 24px' }}>
          <div style={{ maxWidth: '760px' }}>
            <div className="royal-eyebrow royal-eyebrow-on-dark" style={{ marginBottom: '28px' }} data-testid="hero-eyebrow">
              UK Door, Window &amp; Lock Specialists
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 5.5vw, 4rem)',
              fontWeight: '700',
              lineHeight: '1.12',
              letterSpacing: '0.02em',
              color: 'var(--text-on-dark-primary)',
              marginBottom: '28px'
            }}>
              Specialist Door, Window, <br />and Lock Repairs in <span style={{ color: 'var(--royal-gold-bright)' }}>London &amp; Essex</span>
            </h1>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.7',
              color: 'var(--text-on-dark-muted)',
              marginBottom: '36px',
              maxWidth: '640px'
            }}>
              Expert locksmith, uPVC door mechanism repair, tilt and turn window specialists, and glazing services. Based in Romford — serving residential and commercial properties across London and Essex. Correct diagnosis, quality repairs, guaranteed workmanship.
            </p>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <Link to="/contact" className="royal-btn-primary" data-testid="hero-get-quote-button">
                Get a Fast Quote
              </Link>
              <Link to="/services" className="royal-btn-light" data-testid="hero-services-button">
                Explore Services
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div style={{
              display: 'flex',
              gap: '28px',
              marginTop: '48px',
              paddingTop: '28px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              flexWrap: 'wrap'
            }}>
              {['Fully Insured', 'Guaranteed Work', 'Emergency Available', 'Master-Trade Standards'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={18} style={{ color: 'var(--royal-gold-bright)' }} />
                  <span style={{ color: 'var(--text-on-dark-muted)', fontSize: '0.9rem', letterSpacing: '0.02em' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Repair-First Approach Section */}
      <section className="royal-section" style={{ background: 'var(--royal-white)', paddingTop: '80px', paddingBottom: '40px' }} data-testid="repair-first-section">
        <div className="royal-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <span style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: 'rgba(200, 168, 96, 0.12)',
                color: 'var(--royal-gold)',
                fontSize: '0.85rem',
                fontWeight: '600',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                Our Repair-First Approach
              </span>
              <h2 className="royal-heading-lg" style={{ marginBottom: '24px' }}>
                Specialists in the Faults Others Walk Away From
              </h2>
            </div>

            <div style={{
              fontSize: '1.05rem',
              lineHeight: '1.8',
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              <p style={{ marginBottom: '20px' }}>
                At Royal Security, we specialise in complex faults that others walk away from.
                From <strong>multipoint lock repairs</strong> and <strong>uPVC door mechanism repair</strong> to specialist
                <strong> tilt and turn window repair</strong> and servicing, we focus on durable solutions rather than
                quick replacements. Based in Romford, we serve homeowners, landlords, and property managers across
                London and Essex, ensuring your security hardware works perfectly and lasts longer.
              </p>
              <p style={{ marginBottom: '0' }}>
                Whether you need a <strong>patio door lock repair</strong>, an <strong>emergency window repair in London</strong>,
                or a trusted <strong>multipoint lock specialist</strong> to diagnose a stiff or dropped door, our engineers
                bring the right parts, the right tools, and the right experience the first time. Honest assessments,
                guaranteed workmanship, and no pressure to replace what can be properly fixed.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
              marginTop: '32px'
            }}>
              {[
                'Multipoint Lock Repairs',
                'uPVC Door Mechanism Repair',
                'Tilt & Turn Window Repair',
                'Patio Door Lock Repair',
                'Emergency Window Repair London',
              ].map((label, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 16px',
                  background: 'var(--bg-section-alt)',
                  borderLeft: '3px solid var(--royal-gold)',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  color: 'var(--royal-navy)'
                }}>
                  <CheckCircle size={18} style={{ color: 'var(--royal-gold)', flexShrink: 0 }} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="royal-section" style={{ background: 'var(--royal-white)', paddingTop: '40px' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Our Specialist Services</h2>
            <p className="royal-body-lg">
              We're not a generic locksmith. Royal Security specialises in complex door, window, and glazing systems with a repair-first approach.
            </p>
          </div>

          <div className="royal-grid royal-grid-3">
            {services.map((service, index) => (
              <div key={index} className="royal-card scroll-animate">
                <div className="royal-icon-badge">
                  {service.icon}
                </div>
                <h3 className="royal-heading-sm">{service.title}</h3>
                <p className="royal-body">{service.description}</p>
                <Link to="/services" style={{
                  color: 'var(--royal-gold)',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '16px',
                  transition: 'gap 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.gap = '12px'}
                onMouseLeave={(e) => e.currentTarget.style.gap = '8px'}
                >
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Why Royal Security?</h2>
            <p className="royal-body-lg">
              We're built around trust, quality, and long-term reliability. Not rushed fixes.
            </p>
          </div>

          <div className="royal-grid royal-grid-2">
            {whyChoose.map((item, index) => (
              <div key={index} className="scroll-animate" style={{
                display: 'flex',
                gap: '20px',
                padding: '32px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  flexShrink: 0,
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, var(--royal-dark-charcoal) 0%, var(--royal-dark-elevated) 100%)',
                  border: '1px solid var(--royal-gold-border)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--royal-gold)'
                }}>
                  {item.icon}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: 'var(--royal-navy)',
                    marginBottom: '8px'
                  }}>
                    {item.title}
                  </h3>
                  <p className="royal-body">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="royal-heading-lg">Who We Work With</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '32px',
              marginTop: '48px'
            }}>
              <div style={{ textAlign: 'center' }} className="scroll-animate-scale">
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--royal-dark-charcoal) 0%, var(--royal-dark-elevated) 100%)',
                  border: '1px solid var(--royal-gold-border)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'var(--royal-gold)'
                }}>
                  <FileCheck size={36} />
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                  Letting Agents
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                  Reduce admin, reliable reporting
                </p>
              </div>
              <div style={{ textAlign: 'center' }} className="scroll-animate-scale">
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--royal-dark-charcoal) 0%, var(--royal-dark-elevated) 100%)',
                  border: '1px solid var(--royal-gold-border)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'var(--royal-gold)'
                }}>
                  <Users size={36} />
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                  Property Managers
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                  Long-term partnerships
                </p>
              </div>
              <div style={{ textAlign: 'center' }} className="scroll-animate-scale">
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--royal-dark-charcoal) 0%, var(--royal-dark-elevated) 100%)',
                  border: '1px solid var(--royal-gold-border)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'var(--royal-gold)'
                }}>
                  <Shield size={36} />
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                  Landlords & Homeowners
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                  Quality, trusted service
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Service Areas & Expertise */}
      <section className="royal-section" style={{ background: '#f8f9fa' }}>
        <div className="royal-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="royal-heading-lg" style={{ textAlign: 'center', marginBottom: '40px' }}>
              Professional Door, Window & Lock Services Across London and Essex
            </h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '32px',
              marginBottom: '40px'
            }}>
              {/* Locksmith Services */}
              <div className="scroll-animate">
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--royal-navy)', marginBottom: '12px' }}>
                  Expert Locksmith Services
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '12px' }}>
                  Royal Security provides comprehensive locksmith services including multipoint locking system repairs, 
                  lock replacements, emergency lockouts, and security upgrades. We specialise in Euro cylinder locks, 
                  mortice locks, and high-security systems for both residential and commercial properties.
                </p>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Our locksmiths are experienced with all major brands including Yale, ERA, Avocet, and Ultion locks. 
                  Whether you need a broken lock repaired or a complete security upgrade, we provide honest assessments 
                  and quality workmanship.
                </p>
              </div>

              {/* Door Repairs */}
              <div className="scroll-animate">
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--royal-navy)', marginBottom: '12px' }}>
                  uPVC & Composite Door Repairs
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '12px' }}>
                  Doors that won't lock properly, stick, or have dropped are common problems we fix daily. Our door 
                  repair service covers uPVC doors, composite doors, aluminium doors, and French doors. We repair 
                  and replace hinges, handles, letterboxes, and complete mechanisms.
                </p>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We also install and repair tilt and slide doors, bi-fold doors, and patio doors. Our repair-first 
                  approach means we'll always try to fix your existing door before recommending replacement.
                </p>
              </div>

              {/* Window Services */}
              <div className="scroll-animate">
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--royal-navy)', marginBottom: '12px' }}>
                  Tilt & Turn Window Specialists
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '12px' }}>
                  Tilt and turn windows require specialist knowledge that many general window companies don't have. 
                  Royal Security has extensive experience with Schuco, Rehau, VEKA, and other European tilt and turn 
                  systems. We repair faulty mechanisms, replace worn hinges, and fix windows that won't close properly.
                </p>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Our window services also include casement window repairs, sash window locks, window handle replacements, 
                  and glazing services including misted unit replacement.
                </p>
              </div>
            </div>

            {/* Service Areas */}
            <div className="scroll-animate" style={{ 
              background: 'white', 
              padding: '32px', 
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }} data-testid="areas-we-cover">
              <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--royal-navy)', marginBottom: '16px' }}>
                Areas We Cover
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                Based in Romford, Royal Security serves customers throughout <strong>London</strong> and <strong>Essex</strong>. 
                Our regular service areas include:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '8px 24px',
                color: 'var(--text-secondary)'
              }}>
                <span>• Romford</span>
                <span>• Hornchurch</span>
                <span>• Upminster</span>
                <span>• Ilford</span>
                <span>• Canary Wharf</span>
                <span>• Stratford</span>
                <span>• Brentwood</span>
                <span>• Central London</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginTop: '20px' }}>
                We also cover Barking, Dagenham, Walthamstow, Chelmsford, Colchester, Southend, Basildon, and Grays.
                Can&apos;t see your area? <Link to="/contact" style={{ color: 'var(--royal-gold)', fontWeight: '500' }}>Contact us</Link> – 
                we likely cover it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Footer CTA Banner — Rich Royal Charcoal */}
      <section className="royal-crown-texture" style={{
        background: 'radial-gradient(ellipse at center, #262626 0%, #141414 100%)',
        borderTop: '2px solid var(--royal-gold)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.3)',
        padding: '88px 0',
        color: 'var(--royal-white)',
        position: 'relative'
      }} data-testid="pre-footer-cta">
        <div className="royal-container" style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <div className="royal-eyebrow royal-eyebrow-on-dark" style={{ marginBottom: '24px' }}>
            Trusted Across London &amp; Essex
          </div>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 4vw, 2.9rem)',
            fontWeight: '700',
            letterSpacing: '0.02em',
            marginBottom: '20px',
            color: 'var(--royal-white)'
          }}>
            Ready for Specialist Door &amp; Lock Precision?
          </h2>
          <p style={{
            fontSize: '1.15rem',
            lineHeight: '1.7',
            marginBottom: '36px',
            color: 'var(--text-on-dark-muted)',
            maxWidth: '640px',
            margin: '0 auto 36px'
          }}>
            Speak directly with our senior engineer in Romford. Fast response across London &amp; Essex — emergency call-out available.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" data-testid="cta-get-quote-button">
              Request a Quote
            </Link>
            <a href="tel:07710613783" className="royal-btn-light" data-testid="cta-call-button">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
